import React, { memo, useMemo } from 'react'
import GridLayout from 'react-grid-layout'
import { Icon, Intent } from '@blueprintjs/core'

import { IconName, IconNames } from '@blueprintjs/icons'
import CX from 'classnames'
import uniq from 'lodash.uniq'
import clonedeep from 'lodash.clonedeep'
import compiler from './Engine'
import { FormRendererSwitcher } from './renderers'
import { UprightStyle } from '../../../../types'
import { stringToBool } from '../../../../utils/string'

const LAYOUT_DEFAULTS = {
  minW: 2,
  maxW: 6,
  maxH: 4
}

const styles: UprightStyle = {
  duplicateInput: { marginLeft: 25, cursor: 'pointer' }
}

interface Props {
  layout: any[]
  form: any[]
  container: Record<string, any>
  onInputChange: (index, input: Record<string, any>) => void
  previewMode: boolean
  onConfigureInput: (index) => void | undefined
  onFormPreviewChange: (index, value) => void
  onLayoutChange: (newLayout) => void | undefined
  onDuplicateInputClick: (index) => void | undefined
  templateId?: number
}

interface RendererProps extends Props {
  requiredLabelIconName?: IconName
}

function getSortOrder(combined) {
  const sortOrderInner = {}
  let combinedInner = clonedeep(combined)

  const allVariableNames = combinedInner.map(c => c.formItem.variableName).filter(String)

  const allTheHiddenExpressions = uniq(
    combinedInner
      .map(c => c.formItem.props?.hidden) // please eslint
      .filter(String)
  ).join('')
  const variableNames = allVariableNames.filter(variableName => allTheHiddenExpressions.includes(variableName))

  /* eslint-disable no-param-reassign */
  variableNames.forEach(variableName => {
    combinedInner.forEach(c => {
      c.isDependent = (c.formItem.props?.hidden || '').includes(variableName)
    })

    const sortKeyOfVariableName = combinedInner
      .filter(c => !c.isDependent)
      .findIndex(c => c.formItem.variableName === variableName)

    let otherFieldIndex = 0
    let dependentFieldIndex = 0.01
    combinedInner.forEach(c => {
      if (c.isDependent) {
        c.sortKey = sortKeyOfVariableName + dependentFieldIndex
        dependentFieldIndex += 0.01
      } else {
        c.sortKey = otherFieldIndex
        otherFieldIndex += 1
      }
    })

    combinedInner = combinedInner.sort((a, b) => a.sortKey - b.sortKey)
  })

  combinedInner.forEach((c, i) => {
    sortOrderInner[c.item.i] = i
  })

  return sortOrderInner
}

const RendererCore = (props: Props) => {
  const { previewMode, form, onDuplicateInputClick } = props
  const { container } = props
  const layout = props.layout.map(l => ({ ...LAYOUT_DEFAULTS, ...l }))

  let combined = layout.map((item, index) => {
    const formItem = form[index]
    return { item, formItem, index }
  })

  const sortOrder = useMemo(() => {
    if (container.cols !== 1) return {}

    return getSortOrder(combined)

    // We are making _some_ assumptions suppressing the linter here. But ExpressList will
    // ensure that a new templateId is provided any time the template changes (and this needs to be recalculated).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.templateId, container.cols])

  if (container.cols === 1) {
    combined = combined.sort((a, b) => sortOrder[a.item.i] - sortOrder[b.item.i])
  }

  const children = combined.map((c, mapIndex) => {
    const { item, formItem, index } = c

    return (
      <div
        className="ui-grid-box"
        key={item.i}
        style={{
          zIndex: form[index]?.type === 'combobox' ? layout.length - mapIndex : 0,
          display: previewMode && stringToBool(compiler(formItem.props?.hidden, form, false, false)) ? 'none' : 'block'
        }}
      >
        <FormRendererSwitcher
          input={formItem}
          index={index}
          onFormPreviewChange={props.onFormPreviewChange}
          onInputChange={props.onInputChange}
          previewMode={previewMode}
        />
        {!previewMode && <div className="ui-blocker" />}
        {!previewMode && (
          <Icon icon={IconNames.COG} onClick={() => props.onConfigureInput(index)} className="ui-configure-btn" />
        )}
        {!previewMode && (
          <Icon
            icon={IconNames.DUPLICATE}
            onClick={() => onDuplicateInputClick(index)}
            className="ui-configure-btn"
            style={styles.duplicateInput}
          />
        )}
        {!previewMode && formItem.props?.hidden && (
          <Icon intent={Intent.WARNING} icon={IconNames.EYE_OFF} className="ui-hidden-indicator" />
        )}
      </div>
    )
  })

  if (container.cols === 1) {
    return <div className={CX('product-attributes-container', previewMode ? '' : 'ui-layout')}>{children}</div>
  }

  return (
    <div className={previewMode ? '' : 'ui-layout'}>
      <GridLayout
        onLayoutChange={previewMode ? () => {} : props.onLayoutChange}
        className="layout"
        compactType={null}
        preventCollision
        layout={layout}
        cols={container.cols}
        rowHeight={container.rowHeight}
        width={container.width}
        isDraggable={!previewMode}
        isResizable={!previewMode}
        draggableCancel=".ui-configure-btn, .ui-hidden-indicator, .help-popover, .bp3-popover-target"
      >
        {children}
      </GridLayout>
    </div>
  )
}

// For code that wants to add requiredLabelIconName support
export const Renderer = memo((props: RendererProps) => {
  const { form, ...rest } = props
  const form2 = form.map((e: any) => ({
    ...e,
    props: { ...e.props, requiredLabelIconName: props.requiredLabelIconName || null }
  }))
  return <RendererCore form={form2} {...rest} />
})

export default RendererCore
